/**
 * Painel do ambiente (redesign 29/10/2026) — o mesmo em todas as telas do
 * Legado que abrem um ambiente da planilha.
 *
 * Desktop: desliza da direita (480 px, mola). Celular: bottom sheet de 90vh
 * com alça — arrastar para baixo fecha. Fecha também pelo fundo, pelo × e
 * pelo Esc; prende o foco enquanto aberto e devolve ao fechar. Mostra o
 * resumo (pessoas, datas, valor com "segurar para ver"), Terceiros e
 * Acabamentos (recolhíveis), a linha do tempo e, abaixo, carga na
 * marcenaria e o histórico de comentários.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { FiCheck, FiChevronDown, FiEdit2, FiEye, FiX } from "react-icons/fi";

import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { buildAmbienteEtapas } from "../../utils/ambienteTimeline";
import { formatarData, formatarMoeda } from "../../utils/calculos";
import { getAmbienteCurrentStatus } from "../../utils/clientes";
import { toDisplayText } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { indiceDeIdentidade } from "../../utils/identidadeAmbiente";
import CargaNaMarcenaria from "../CargaNaMarcenaria";
import HistoricoAmbiente from "../HistoricoAmbiente";
import { ProjetistaNaLinhaDoTempo } from "../ProjetistaExecutivo";
import { bloqueioDoProjetista } from "../ProjetistaExecutivo/regras";
import { acabamentosDoAmbiente, datasDoAmbiente, pessoasDoAmbiente, terceirosDoAmbiente } from "./dadosDoPainel";
import "../../styles/canonical/redesign-v2.css";
import "./painel-ambiente.css";

const MOLA = { type: "spring", stiffness: 380, damping: 34, mass: 0.9 };
const SAIDA = { duration: 0.26, ease: [0.4, 0, 1, 1] };
const OCULTO = "••••••";
const COR_DO_PAPEL = { vendedor: "var(--c2)", consultor: "var(--accent)", marceneiro: "var(--warn)" };
/** Fecha o bottom sheet: arrasto além de 30% da altura ou mais rápido que 0,5 px/ms. */
const FRACAO_PARA_FECHAR = 0.3;
const VELOCIDADE_PARA_FECHAR = 500;
const TOM_DA_VARIANTE = { success: "ok", lime: "acc", warning: "warn", orange: "warn", danger: "bad", info: "acc", today: "acc", purple: "vio" };

function tomDoStatus(status, variante) {
  return status === "Entregue" ? "ok" : TOM_DA_VARIANTE[variante] || "neu";
}

function useCelular() {
  const consulta = "(max-width: 767px)";
  const [celular, setCelular] = useState(() => typeof window !== "undefined" && window.matchMedia?.(consulta).matches);
  useEffect(() => {
    const mql = window.matchMedia?.(consulta);
    if (!mql) return undefined;
    const mudar = (e) => setCelular(e.matches);
    mql.addEventListener("change", mudar);
    return () => mql.removeEventListener("change", mudar);
  }, []);
  return Boolean(celular);
}

/** Esc fecha, Tab circula dentro do painel, rolagem da página travada; o foco volta ao fechar. */
function useModal(aberto, painelRef, aoFechar) {
  useEffect(() => {
    if (!aberto) return undefined;
    const anterior = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focar = requestAnimationFrame(() => painelRef.current?.querySelector("[data-fechar]")?.focus());
    function tecla(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        aoFechar();
        return;
      }
      if (e.key !== "Tab" || !painelRef.current) return;
      const focaveis = [...painelRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      )].filter((el) => !el.closest("[inert]"));
      if (!focaveis.length) return;
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    }
    document.addEventListener("keydown", tecla, true);
    return () => {
      cancelAnimationFrame(focar);
      document.removeEventListener("keydown", tecla, true);
      document.body.style.overflow = overflow;
      if (anterior && typeof anterior.focus === "function") anterior.focus();
    };
  }, [aberto, painelRef, aoFechar]);
}

function iniciais(nome) {
  return String(nome).split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

/** Valor do ambiente: com a privacidade ligada, aparece só enquanto o olho fica pressionado. */
function ValorDoAmbiente({ valor }) {
  const { precosVisiveis, canViewPrices } = usePrecosVisiveis() || {};
  const [segurando, setSegurando] = useState(false);
  const mostrar = precosVisiveis || segurando;
  const soltar = () => setSegurando(false);
  return (
    <div className="cl-dsc-v">
      <span>Valor do ambiente</span>
      <span className="cl-dsc-vv">
        <b data-revelado={mostrar}>
          <span className="oculto" aria-hidden={mostrar}>{OCULTO}</span>
          <span className="valor" aria-hidden={!mostrar}>{canViewPrices ? formatarMoeda(valor) : OCULTO}</span>
        </b>
        {canViewPrices && !precosVisiveis && (
          <button
            type="button"
            className="cl-peek"
            aria-pressed={segurando}
            aria-label="Segure para ver o valor"
            title="Segure para ver o valor"
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture?.(e.pointerId);
              setSegurando(true);
            }}
            onPointerUp={soltar}
            onPointerLeave={soltar}
            onPointerCancel={soltar}
            onBlur={soltar}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                setSegurando(true);
              }
            }}
            onKeyUp={soltar}
            onContextMenu={(e) => e.preventDefault()}
          >
            <FiEye size={16} aria-hidden="true" />
          </button>
        )}
      </span>
    </div>
  );
}

function Resumo({ row }) {
  const pessoas = pessoasDoAmbiente(row);
  const datas = datasDoAmbiente(row);
  return (
    <div className="cl-dsc">
      <div className="cl-dsc-p">
        {pessoas.map((p) => (
          <span key={p.papel} className="cl-dsc-pp">
            <i style={{ background: p.definido ? COR_DO_PAPEL[p.papel] : "var(--surface-3)", color: p.definido ? undefined : "var(--ink-3)" }}
              aria-hidden="true">{p.definido ? iniciais(p.nome) : "—"}</i>
            <span><b className={p.definido ? undefined : "vazio"}>{p.nome}</b><em>{p.papel}</em></span>
          </span>
        ))}
      </div>
      <div className="cl-dsc-g">
        {datas.map((d) => (
          <div key={d.rotulo} className="cl-dsc-c">
            <span>{d.rotulo}</span>
            <b className={d.alerta ? "alerta" : undefined} title={d.alerta ? "Diferente da entrega prevista" : undefined}>{d.valor}</b>
          </div>
        ))}
      </div>
      <ValorDoAmbiente valor={row?.[F.VALOR_DO_AMBIENTE]} />
    </div>
  );
}

function Recolhivel({ aberto, children }) {
  return (
    <div className="cl-recolhivel" data-aberto={aberto}>
      <div inert={!aberto}>{children}</div>
    </div>
  );
}

function Bloco({ titulo, resumo, tomDoResumo, aberto, aoAlternar, barra, fechado, children }) {
  return (
    <section className="cl-blk" data-aberto={aberto} aria-label={titulo}>
      <button type="button" className="cl-blk-t" aria-expanded={aberto} onClick={aoAlternar}>
        <span className="cl-eyebrow">{titulo}</span>
        <span className={`cl-blk-s${tomDoResumo ? ` ${tomDoResumo}` : ""}`}>{resumo}</span>
        <span className="cl-ch" aria-hidden="true"><FiChevronDown size={14} /></span>
      </button>
      {barra}
      <Recolhivel aberto={!aberto}>{fechado}</Recolhivel>
      <Recolhivel aberto={aberto}>{children}</Recolhivel>
    </section>
  );
}

function IconeDaSituacao({ ok }) {
  return ok ? <FiCheck size={13} strokeWidth={2.6} /> : (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8" /><path d="M12 8v4l2.5 2" />
    </svg>
  );
}

function CategoriaDeTerceiro({ categoria }) {
  const [aberta, setAberta] = useState(!categoria.ok);
  return (
    <div className={`cl-tc${categoria.ok ? "" : " pend"}`}>
      <button type="button" className="cl-tc-h" aria-expanded={aberta} onClick={() => setAberta((a) => !a)}>
        <span className={`cl-tc-ic ${categoria.ok ? "ok" : "pend"}`}><IconeDaSituacao ok={categoria.ok} /></span>
        <span className="cl-tc-n">{categoria.rotulo}</span>
        <span className={`cl-tc-c${categoria.ok ? "" : " pend"}`}>
          {categoria.ok ? "OK" : `${categoria.itens.length} pend.`}
        </span>
        <span className="cl-ch" aria-hidden="true"><FiChevronDown size={14} /></span>
      </button>
      <Recolhivel aberto={aberta}>
        <ul className="cl-tc-items">
          {categoria.itens.map((item, i) => (
            <li key={`${categoria.id}-${i}`} className="cl-tc-i">
              <span className="cl-tc-d">{item}{i === 0 && categoria.qtd && <small>qtd. {categoria.qtd}</small>}</span>
              <span className={`cl-pill sm ${categoria.ok ? "p-ok" : "p-warn"}`}><span>{categoria.situacao}</span></span>
            </li>
          ))}
        </ul>
      </Recolhivel>
    </div>
  );
}

function Terceiros({ row, otimizado }) {
  const [aberto, setAberto] = useState(false);
  const t = useMemo(() => terceirosDoAmbiente(row), [row]);
  if (!t.total) {
    return (
      <section className="cl-blk" aria-label="Terceiros">
        <div className="cl-blk-t" style={{ cursor: "default" }}>
          <span className="cl-eyebrow">Terceiros</span>
          <span className="cl-blk-s neu">{otimizado ? "sem itens" : "definidos na otimização"}</span>
        </div>
      </section>
    );
  }
  const pendente = t.ok < t.total;
  const resumo = `${t.ok} de ${t.total} OK${t.itensPendentes ? ` · ${t.itensPendentes} ${t.itensPendentes === 1 ? "item pendente" : "itens pendentes"}` : ""}`;
  return (
    <Bloco
      titulo="Terceiros"
      resumo={resumo}
      tomDoResumo={pendente ? "warn" : ""}
      aberto={aberto}
      aoAlternar={() => setAberto((a) => !a)}
      barra={(
        <div className="cl-blk-bar" role="img" aria-label={`${t.ok} de ${t.total} categorias recebidas`}>
          <motion.i initial={{ scaleX: 0 }} animate={{ scaleX: t.ok / t.total }} transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1], delay: 0.2 }} />
        </div>
      )}
      fechado={(
        <div className="cl-mini">
          {t.categorias.map((c) => (
            <span key={c.id} className={`cl-mc${c.ok ? "" : " pend"}`}><i />{c.rotulo}{!c.ok && <b>{c.itens.length}</b>}</span>
          ))}
        </div>
      )}
    >
      <div className="cl-tcs">{t.categorias.map((c) => <CategoriaDeTerceiro key={c.id} categoria={c} />)}</div>
    </Bloco>
  );
}

function Pincel() {
  return <FiEdit2 size={12} aria-hidden="true" />;
}

function Acabamentos({ row }) {
  const [aberto, setAberto] = useState(false);
  const a = useMemo(() => acabamentosDoAmbiente(row), [row]);
  if (!a.total && !a.descritivo) {
    return (
      <section className="cl-blk" aria-label="Acabamentos">
        <div className="cl-blk-t" style={{ cursor: "default" }}>
          <span className="cl-eyebrow">Acabamentos</span>
          <span className="cl-blk-s neu">sem acabamento especial</span>
        </div>
      </section>
    );
  }
  return (
    <Bloco
      titulo="Acabamentos"
      resumo={a.total ? `${a.ok} de ${a.total} OK` : "ver descrição"}
      tomDoResumo={a.ok < a.total ? "warn" : ""}
      aberto={aberto}
      aoAlternar={() => setAberto((v) => !v)}
      fechado={(
        <div className="cl-mini">
          {a.itens.map((i) => (
            <span key={i.id} className={`cl-mc${i.ok ? "" : " pend"}`}>
              <i className="pincel"><Pincel /></i>{i.rotulo}
            </span>
          ))}
        </div>
      )}
    >
      <div className="cl-acs">
        {a.itens.map((i) => (
          <div key={i.id} className="cl-ac-i">
            {/* A planilha não aponta a cor do cadastro: amostra neutra com pincel. */}
            <span className="cl-ac-sw" aria-hidden="true"><Pincel /></span>
            <span className="cl-ac-t"><b>{i.rotulo}</b>{i.qtd && <small>qtd. {i.qtd}</small>}</span>
            <span className={`cl-pill sm ${i.ok ? "p-ok" : "p-warn"}`}><span>{i.situacao}</span></span>
          </div>
        ))}
        {a.descritivo && <div className="cl-ac-desc">{a.descritivo}</div>}
      </div>
    </Bloco>
  );
}

function Relogio() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8" /><path className="ponteiro" d="M12 8v4l2.5 2" />
    </svg>
  );
}

function rotuloDaData(etapa) {
  if (etapa.status === "atual") return "em andamento";
  if (etapa.status === "futura") return "aguardando";
  if (etapa.implicita) return "concluído (implícito)";
  const data = formatarData(etapa.data);
  return data === "—" ? "concluído" : data;
}

const CLASSE_DA_ETAPA = { concluida: "feita", atual: "atual", futura: "futura" };

function LinhaDoTempo({ etapas, extras }) {
  return (
    <div className="cl-tl">
      {etapas.map((etapa, i) => (
        <motion.div key={etapa.key} className={`cl-tl-i ${CLASSE_DA_ETAPA[etapa.status]}`}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1], delay: 0.18 + Math.min(i, 8) * 0.035 }}>
          <span className="cl-tl-d">
            {etapa.status === "concluida" && <FiCheck size={14} strokeWidth={2.6} aria-hidden="true" />}
            {etapa.status === "atual" && <Relogio />}
          </span>
          <div>
            <div className="cl-tl-n">{etapa.titulo}</div>
            <div className="cl-tl-s">{rotuloDaData(etapa)}</div>
            {extras?.[etapa.key]}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function Conteudo({ ambiente, celular, controlesDeArrasto, aoFechar }) {
  const row = ambiente.row;
  const { dados } = useDados() || {};
  const status = useMemo(() => (row ? getAmbienteCurrentStatus(row) : null), [row]);
  const etapas = useMemo(() => (row ? buildAmbienteEtapas(row) : []), [row]);
  const extras = useMemo(() => {
    if (!row) return undefined;
    const indice = Array.isArray(dados) ? indiceDeIdentidade(dados) : null;
    return { executivo: <ProjetistaNaLinhaDoTempo row={row} bloqueio={bloqueioDoProjetista(row, indice)} /> };
  }, [row, dados]);
  const pilulas = status ? [{ status: status.status, statusVariant: status.statusVariant }, ...(status.pendencias || [])] : [];
  const lote = ambiente.lote || toDisplayText(row?.[F.NUMERO_DO_LOTE]);
  const iniciarArrasto = (e) => celular && controlesDeArrasto.start(e);

  return (
    <>
      <header className="cl-dr-h" onPointerDown={iniciarArrasto}>
        <button type="button" className="cl-dr-alca" aria-label="Arraste para baixo para fechar" tabIndex={-1} />
        <div className="cl-dr-t">
          <div style={{ minWidth: 0 }}>
            <h2 id="clp-titulo">{ambiente.ambiente || toDisplayText(row?.[F.AMBIENTE])}</h2>
            <div className="cl-dr-st">
              {pilulas.map((p) => (
                <span key={p.status} className={`cl-pill p-${tomDoStatus(p.status, p.statusVariant)}`} title={p.status}>
                  <span>{p.status}</span>
                </span>
              ))}
            </div>
          </div>
          <button type="button" className="cl-dr-x" aria-label="Fechar" data-fechar onClick={aoFechar}
            onPointerDown={(e) => e.stopPropagation()}>
            <FiX size={16} />
          </button>
        </div>
        <div className="cl-dr-m">
          <span>Cliente <b>{toDisplayText(row?.[F.NOME_DO_CLIENTE])}</b></span>
          <span>Pedido <b>{toDisplayText(row?.[F.NUMERO_DO_PEDIDO])}</b></span>
          <span>Lote <b>{lote}</b></span>
        </div>
      </header>
      <div className="cl-dr-b">
        {row && (
          <>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: 0.08 }}>
              <Resumo row={row} />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: 0.12 }}>
              <Terceiros row={row} otimizado={Boolean(row[F.OTIMIZACAO_DO_LOTE])} />
              <Acabamentos row={row} />
            </motion.div>
          </>
        )}
        <div className="cl-sec">
          <div className="cl-eyebrow" style={{ marginBottom: 4 }}>Linha do tempo</div>
          <LinhaDoTempo etapas={etapas} extras={extras} />
        </div>
        {row && <div className="cl-sec"><CargaNaMarcenaria row={row} /></div>}
        {row && <div className="cl-sec"><HistoricoAmbiente row={row} /></div>}
      </div>
    </>
  );
}

export default function AmbienteDetalhesDrawer({ ambiente, onClose }) {
  const celular = useCelular();
  const painelRef = useRef(null);
  const controlesDeArrasto = useDragControls();
  const fechar = useCallback(() => onClose?.(), [onClose]);
  useModal(Boolean(ambiente), painelRef, fechar);

  function aoSoltar(_, info) {
    const altura = painelRef.current?.offsetHeight || window.innerHeight;
    if (info.offset.y > altura * FRACAO_PARA_FECHAR || info.velocity.y > VELOCIDADE_PARA_FECHAR) fechar();
  }

  const movimento = celular
    ? { initial: { y: "100%" }, animate: { y: 0, transition: MOLA }, exit: { y: "100%", transition: SAIDA } }
    : { initial: { x: "100%" }, animate: { x: 0, transition: MOLA }, exit: { x: "100%", transition: SAIDA } };

  if (typeof document === "undefined") return null;
  return createPortal(
    <AnimatePresence>
      {ambiente && (
        <motion.div key="clp-scrim" className="clp-scrim" role="presentation" onClick={fechar}
          initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { duration: 0.3 } }} exit={{ opacity: 0, transition: SAIDA }} />
      )}
      {ambiente && (
        <motion.div
          key="clp-painel"
          ref={painelRef}
          className="clp"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clp-titulo"
          {...movimento}
          drag={celular ? "y" : false}
          dragControls={controlesDeArrasto}
          dragListener={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0.04, bottom: 1 }}
          onDragEnd={aoSoltar}
        >
          <Conteudo ambiente={ambiente} celular={celular} controlesDeArrasto={controlesDeArrasto} aoFechar={fechar} />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
